import React, { useEffect, useMemo, useState, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PromoBanner from "./PromoBanner";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { CartContext } from "./CartContext";

function stripUnsafe(html = "") {
  // mini “sanitization” (évite script/iframe). Idéalement DOMPurify plus tard.
  return String(html)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, "");
}

const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart } = useContext(CartContext);

  const [product, setProduct] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addedMessage, setAddedMessage] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/products/${id}`)
      .then((res) => res.json())
      .then((data) => {
        setProduct(data);
        setSelectedIndex(0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  const colors = product?.colors || [];
  const activeIdx = hoveredIndex !== null ? hoveredIndex : selectedIndex;
  const selectedColor = colors?.[activeIdx] || null;

  const displayedImage = selectedColor?.image_url || product?.image;

  const hasPromo = !!product?.promo_active && !!product?.promo_price;
  const displayPrice = hasPromo ? Number(product.promo_price) : Number(product?.price || 0);

  const pageTitle = product ? `${product.name} – Sac en crochet fait main | Deenzine` : "Produit | Deenzine";
  const descriptionText = useMemo(() => {
    const raw = product?.description ? product.description.replace(/<[^>]*>/g, " ") : "";
    const clean = raw.replace(/\s+/g, " ").trim();
    if (!clean) return "Sac en crochet fait main, pièce unique, expédition rapide.";
    return clean.slice(0, 155);
  }, [product]);

  const canonical = `https://www.deenzine.com/product/${id}`;

  const jsonLd = useMemo(() => {
    if (!product) return null;
    const image = displayedImage?.startsWith("http")
      ? displayedImage
      : `https://www.deenzine.com${displayedImage?.startsWith("/") ? "" : "/"}${displayedImage || ""}`;

    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      image: image ? [image] : [],
      description: descriptionText,
      sku: String(product.id),
      brand: { "@type": "Brand", name: "Deenzine" },
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        price: String(displayPrice),
        availability: "https://schema.org/InStock",
        itemCondition: "https://schema.org/NewCondition",
        url: canonical,
        seller: { "@type": "Organization", name: "Deenzine" },
      },
    };
  }, [product, displayedImage, descriptionText, displayPrice, canonical]);

  const handleAddToCart = () => {
    if (!product) return;

    addToCart({
      id: product.id,
      name: product.name,
      price: Number(product.price),
      promo_price: product.promo_price ? Number(product.promo_price) : null,
      promo_active: !!product.promo_active,
      image: displayedImage,
      selectedColor: selectedColor?.color_name || null,
      chaine: selectedColor?.chaine || null,
      stock: selectedColor?.stock ?? null,
      quantity: 1,
    });

    setAddedMessage(true);
    setTimeout(() => setAddedMessage(false), 1600);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="page">
          <section className="section">
            <div className="container">
              <div className="card cardPad">Chargement du produit…</div>
            </div>
          </section>
        </div>
        <Footer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Navbar />
        <div className="page">
          <section className="section">
            <div className="container">
              <div className="card cardPad">
                Produit introuvable. <Link to="/products" className="btn btnSecondary">Retour à la collection</Link>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={descriptionText} />
        <link rel="canonical" href={canonical} />

        {/* Open Graph */}
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="Deenzine" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={descriptionText} />
        {displayedImage && <meta property="og:image" content={
          displayedImage.startsWith("http") ? displayedImage : `https://www.deenzine.com${displayedImage.startsWith("/") ? "" : "/"}${displayedImage}`
        } />}
        <meta property="product:price:amount" content={String(displayPrice)} />
        <meta property="product:price:currency" content="EUR" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={descriptionText} />
        {displayedImage && <meta name="twitter:image" content={
          displayedImage.startsWith("http") ? displayedImage : `https://www.deenzine.com${displayedImage.startsWith("/") ? "" : "/"}${displayedImage}`
        } />}

        {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      </Helmet>

      <PromoBanner />
      <Navbar />

      <div className="page">
        <section className="section">
          <div className="container">
            <div className="grid" style={{ alignItems: "start" }}>
              {/* Images */}
              <div style={{ gridColumn: "span 6" }}>
                <div className="card" style={{ overflow: "hidden" }}>
                  <img
                    src={displayedImage}
                    alt={product.name}
                    style={{ width: "100%", height: "auto", display: "block" }}
                    loading="eager"
                  />
                </div>

                <div className="card cardPad" style={{ marginTop: 14 }}>
                  <div className="badge">🚚 Expédition rapide</div>
                  <div style={{ height: 10 }} />
                  <div className="badge">🔒 Paiement sécurisé Stripe</div>
                  <div style={{ height: 10 }} />
                  <div className="badge">🧶 Fait main • Pièce unique</div>
                </div>
              </div>

              {/* Détails */}
              <div style={{ gridColumn: "span 6" }}>
                <div className="card cardPad">
                  <h1 className="h2" style={{ marginBottom: 8 }}>{product.name}</h1>

                  <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                    {hasPromo ? (
                      <>
                        <span style={{ textDecoration: "line-through", opacity: 0.55 }}>
                          {Number(product.price).toFixed(2)} €
                        </span>
                        <span style={{ fontWeight: 900, color: "var(--brand)", fontSize: 22 }}>
                          {Number(product.promo_price).toFixed(2)} €
                        </span>
                        <span className="badge">Promo</span>
                      </>
                    ) : (
                      <span style={{ fontWeight: 900, fontSize: 22 }}>
                        {Number(product.price).toFixed(2)} €
                      </span>
                    )}
                  </div>

                  <div className="hr" />

                  <div
                    className="p"
                    style={{ color: "var(--muted)" }}
                    dangerouslySetInnerHTML={{ __html: stripUnsafe(product.description || "") }}
                  />

                  {/* Couleurs */}
                  {colors?.length > 0 && (
                    <>
                      <div className="hr" />
                      <div style={{ fontWeight: 900, marginBottom: 10 }}>Choisir une couleur</div>

                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {colors.map((c, idx) => {
                          const active = idx === activeIdx;
                          return (
                            <button
                              key={`${c.color_name}-${idx}`}
                              type="button"
                              title={c.color_name}
                              aria-label={`Couleur ${c.color_name}`}
                              onMouseEnter={() => setHoveredIndex(idx)}
                              onMouseLeave={() => setHoveredIndex(null)}
                              onClick={() => setSelectedIndex(idx)}
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 999,
                                background: c.color_code || "#ddd",
                                border: active ? "2px solid rgba(255,255,255,.9)" : "1px solid rgba(0,0,0,.14)",
                                outline: active ? "2px solid rgba(43,29,24,.7)" : "none",
                                cursor: "pointer",
                              }}
                            />
                          );
                        })}
                      </div>

                      <div style={{ marginTop: 10 }} className="p">
                        Couleur sélectionnée : <strong>{selectedColor?.color_name || "-"}</strong>
                        {selectedColor?.stock != null && (
                          <>
                            {" "}
                            • Stock :{" "}
                            <strong style={{ color: selectedColor.stock <= 1 ? "#b33e3e" : "inherit" }}>
                              {selectedColor.stock}
                            </strong>
                          </>
                        )}
                      </div>
                    </>
                  )}

                  {/* CTA */}
                  <div className="hr" />

                  {addedMessage && (
                    <div className="badge" style={{ marginBottom: 12 }}>
                      ✔️ Ajouté au panier
                    </div>
                  )}

                  <button className="btn" onClick={handleAddToCart} style={{ width: "100%" }}>
                    Ajouter au panier →
                  </button>

                  <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                    <Link className="btn btnSecondary" to="/products" style={{ flex: "1 1 220px" }}>
                      Retour collection
                    </Link>
                    <Link className="btn btnSecondary" to="/cart" style={{ flex: "1 1 220px" }}>
                      Voir le panier
                    </Link>
                  </div>
                </div>

                {/* Détails modèle */}
                <div className="card cardPad" style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 900, marginBottom: 10 }}>Détails du modèle</div>
                  <div className="p">
                    <strong>Dimensions :</strong> {product.dimension || "Non spécifiées"}
                    <br />
                    <strong>Cordon :</strong> {product.cordon || "Non précisé"}
                    <br />
                    <strong>Chaîne :</strong> {selectedColor?.chaine || "Non précisée"}
                  </div>

                  <div className="hr" />

                  <div style={{ fontWeight: 900, marginBottom: 6 }}>FAQ</div>
                  <div className="p">
                    <strong>Livraison :</strong> préparation 1–7 jours ouvrés, expédition rapide.
                    <br />
                    <strong>Retours :</strong> en cas de défaut à réception (48h) avec photo.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default ProductDetail;
